import { z } from "zod";

import type {
  GroupColorSlot,
  GroupId,
  WorkspaceDateRange,
} from "../workspace/workspace.types";
import { GROUP_IDS, getDefaultGroupColor } from "../workspace/workspace.utils";

export type GlobalGroupId = GroupId;

export const GLOBAL_GROUP_IDS = GROUP_IDS;

export interface GlobalGroup {
  id: GlobalGroupId;
  name?: string;
  colorSlot: GroupColorSlot;
  linkState: {
    symbol?: string;
    dateRange?: WorkspaceDateRange;
  };
}

export interface GroupRegistryDocument {
  schemaVersion: 3;
  revision: number;
  groups: GlobalGroup[];
}

const colorSlots = [
  "blue",
  "green",
  "yellow",
  "orange",
  "red",
  "purple",
  "pink",
  "teal",
] as const satisfies readonly GroupColorSlot[];

const dateRangeSchema = z
  .object({
    from: z.iso.date(),
    to: z.iso.date(),
  })
  .strict();

const groupSchema = z
  .object({
    id: z.enum(GROUP_IDS),
    name: z.string().trim().min(1).max(100).optional(),
    colorSlot: z.enum(colorSlots),
    linkState: z
      .object({
        symbol: z.string().trim().min(1).optional(),
        dateRange: dateRangeSchema.optional(),
      })
      .strict(),
  })
  .strict();

const documentSchema = z
  .object({
    schemaVersion: z.literal(3),
    revision: z.number().int().nonnegative(),
    groups: z.array(groupSchema).max(GROUP_IDS.length),
  })
  .strict()
  .superRefine(({ groups }, context) => {
    const seen = new Set<string>();
    for (let index = 0; index < groups.length; index += 1) {
      const group = groups[index];
      if (seen.has(group.id)) {
        context.addIssue({
          code: "custom",
          message: "Global group ids must be unique",
          path: ["groups", index, "id"],
        });
      }
      seen.add(group.id);
    }
    const sorted = [...groups].sort((a, b) => a.id.localeCompare(b.id));
    for (let index = 0; index < groups.length; index += 1) {
      if (groups[index].id !== sorted[index].id) {
        context.addIssue({
          code: "custom",
          message: "Global groups must be sorted by letter id",
          path: ["groups"],
        });
        break;
      }
    }
  });

export const createDefaultGroupRegistryDocument =
  (): GroupRegistryDocument => ({
    schemaVersion: 3,
    revision: 0,
    groups: [],
  });

export const sortGlobalGroups = (groups: GlobalGroup[]): GlobalGroup[] =>
  [...groups].sort((a, b) => a.id.localeCompare(b.id));

export const createGlobalGroup = (
  id: GlobalGroupId,
  options: {
    seedSymbol?: string;
    colorSlot?: GroupColorSlot;
    name?: string;
  } = {},
): GlobalGroup => {
  const group: GlobalGroup = {
    id,
    colorSlot: options.colorSlot ?? getDefaultGroupColor(id),
    linkState: {},
  };
  if (options.name?.trim()) {
    group.name = options.name.trim().slice(0, 100);
  }
  if (options.seedSymbol?.trim()) {
    group.linkState.symbol = options.seedSymbol.trim().toUpperCase();
  }
  return group;
};

export const parseGroupRegistryDocument = (
  value: unknown,
): GroupRegistryDocument | undefined => {
  const parsed = documentSchema.safeParse(value);
  return parsed.success ? (parsed.data as GroupRegistryDocument) : undefined;
};

export const groupRegistryDocumentsEqual = (
  left: GroupRegistryDocument,
  right: GroupRegistryDocument,
): boolean => JSON.stringify(left) === JSON.stringify(right);

export const canonicalGroupRegistryDocument = (
  left: GroupRegistryDocument,
  right: GroupRegistryDocument,
): GroupRegistryDocument =>
  JSON.stringify(left) >= JSON.stringify(right) ? left : right;
